import LayerManagerWidget from './LayerManagerWidget.js';
import LayerConfigBuilderWidget from './LayerConfigBuilderWidget.js';

const STORAGE_KEY = 'lcb_layerConfigs';

export default class CombinedLayerControlWidget {
  constructor({
    view,
    defaultLayerConfigs = [],
    canEditConfig = false,
    managerPosition = 'left',
    builderPosition = 'right',
    managerTitle = 'Layers',
    builderTitle = 'Layer Discovery',
    storageKey = STORAGE_KEY,
    esri = null,
    collapsedByDefault = false,
    loadConfig = null,
    saveConfig = null,
  } = {}) {
    if (!view) throw new Error('[CombinedLayerControlWidget] options.view is required');
    this._view = view;
    this._canEditConfig = !!canEditConfig;
    this._managerPosition = managerPosition;
    this._builderPosition = builderPosition;
    this._managerTitle = managerTitle;
    this._builderTitle = builderTitle;
    this._storageKey = storageKey;
    this._esri = esri;
    this._collapsedByDefault = !!collapsedByDefault;
    this._loadConfig = loadConfig || (async () => {
      try {
        const parsed = JSON.parse(localStorage.getItem(this._storageKey) || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    });
    this._saveConfig = saveConfig || (async (cfg) => {
      localStorage.setItem(this._storageKey, JSON.stringify(cfg, null, 2));
    });
    this._builder = null;
    this._defaultLayerConfigs = defaultLayerConfigs;

    this.layerManager = new LayerManagerWidget({
      view: this._view,
      layerConfigs: this._defaultLayerConfigs,
      position: this._managerPosition,
      title: this._managerTitle,
      mode: 'view',
      canEnterEdit: this._canEditConfig,
      onRequestDiscover: () => this._openDiscovery(),
      onRequestSave: () => this.saveRunningConfig(),
      esri: this._esri,
      collapsed: this._collapsedByDefault,
    });

    this._initializeFromParent();
  }

  async _initializeFromParent() {
    try {
      this.layerManager?.setBusy?.(true);
      const loaded = await this._loadConfig();
      const initial = Array.isArray(loaded) && loaded.length ? loaded : this._defaultLayerConfigs;
      this.layerManager.destroy();
      this.layerManager = new LayerManagerWidget({
        view: this._view,
        layerConfigs: initial,
        position: this._managerPosition,
        title: this._managerTitle,
        mode: 'view',
        canEnterEdit: this._canEditConfig,
        onRequestDiscover: () => this._openDiscovery(),
        onRequestSave: () => this.saveRunningConfig(),
        esri: this._esri,
        collapsed: this._collapsedByDefault,
      });
    } catch {
      // keep defaults if parent load fails
    } finally {
      this.layerManager?.setBusy?.(false);
    }
  }

  _openDiscovery() {
    if (!this._canEditConfig || this._builder) return;
    this._builder = new LayerConfigBuilderWidget({
      view: this._view,
      position: this._builderPosition,
      title: this._builderTitle,
      esri: this._esri,
      onCancel: () => this._closeDiscovery(),
      onSave: (configs) => {
        this.addOrUpdateConfigs(configs);
        this._closeDiscovery();
      },
    });
  }

  _closeDiscovery() {
    this._builder?.destroy();
    this._builder = null;
  }

  addOrUpdateConfigs(configs = []) {
    const merged = [...this.layerManager.getRunningConfig()];
    for (const cfg of configs || []) {
      const idx = merged.findIndex((m) => m.id === cfg.id);
      if (idx >= 0) merged[idx] = { ...merged[idx], ...cfg };
      else merged.push(cfg);
    }
    this.layerManager.destroy();
    this.layerManager = new LayerManagerWidget({
      view: this._view,
      layerConfigs: merged,
      position: this._managerPosition,
      title: this._managerTitle,
      mode: 'edit',
      canEnterEdit: this._canEditConfig,
      onRequestDiscover: () => this._openDiscovery(),
      onRequestSave: () => this.saveRunningConfig(),
      esri: this._esri,
      collapsed: this._collapsedByDefault,
    });
  }

  async saveRunningConfig() {
    this.layerManager?.setBusy?.(true);
    const cfg = this.layerManager.getRunningConfig();
    try {
      await this._saveConfig(cfg);
      return cfg;
    } finally {
      this.layerManager?.setBusy?.(false);
    }
  }

  destroy() {
    this.layerManager?.destroy();
    this._closeDiscovery();
  }
}
